import React, { useState } from "react";
import { api, apiErr } from "../api";
import { useAdminData } from "../useAdminData";
import { useAuth } from "../AuthContext";
import { Panel, Skeleton, EmptyState, ErrorState, PageHeader, Badge, Button, Spinner, fmtNum, fmtDate } from "../Primitives";
import { PLATFORMS } from "../catalog";

const CATALOG_LINKS = PLATFORMS.map((p) => ({ key: p.id, url: p.link }));
import { History, RefreshCw } from "lucide-react";

const STATUS = {
  reachable: ["ok", "HTTP acessível"],
  http_error: ["bad", "Erro HTTP"],
  unreachable: ["bad", "Inacessível"],
  too_many_redirects: ["warn", "Redirecionamentos em excesso"],
  blocked: ["bad", "Bloqueado (segurança)"],
  antibot: ["warn", "Anti-bot (inconclusivo)"],
};
const REFERRAL = {
  preserved: ["ok", "parâmetros mantidos"],
  changed: ["warn", "parâmetros alterados"],
  no_query_params: ["muted", "sem parâmetros na URL"],
  unknown: ["muted", "não verificável"],
};

function StatusBadge({ status }) {
  const [tone, text] = STATUS[status] || ["muted", "Não testado"];
  return <Badge tone={tone}>{text}</Badge>;
}

function MonitorInfo({ settings }) {
  return (
    <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-[13px]" data-testid="monitor-info">
      <div><p className="ops-label">Verificação automática</p><p className="mono">{settings.enabled ? "ativa" : "inativa"}</p></div>
      <div><p className="ops-label">Intervalo</p><p className="mono">{settings.interval_minutes} min</p></div>
      <div><p className="ops-label">Tempo limite</p><p className="mono">{settings.timeout_seconds} s</p></div>
      <div><p className="ops-label">Falhas p/ alerta</p><p className="mono">{settings.failure_threshold}</p></div>
    </div>
  );
}

function HistoryRows({ linkId }) {
  const { data, loading } = useAdminData(async (a) => (await a.get(`/admin/links/${linkId}/history`, { params: { page_size: 10 } })).data, [linkId]);
  if (loading) return <div className="p-3"><Skeleton className="h-16" /></div>;
  if (!data.rows.length) return <p className="ops-help p-3">Nenhum teste registrado.</p>;
  return (
    <table className="ops-table" data-testid={`history-${linkId}`}>
      <tbody>
        {data.rows.map((r) => (
          <tr key={r.id}>
            <td className="mono text-[var(--muted)] whitespace-nowrap">{fmtDate(r.checked_at)}</td>
            <td><StatusBadge status={r.status} /></td>
            <td className="num">{r.http_status ?? "—"}</td>
            <td className="num">{fmtNum(r.response_ms)} ms</td>
            <td className="text-[12px] text-[var(--muted)] max-w-[320px] truncate">{r.error || r.blocked_reason || r.final_url}</td>
            <td className="text-[12px] text-[var(--muted2)]">{r.manual ? "manual" : "automático"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LinkRow({ link, onChange }) {
  const canOperate = !!useAuth().user?.permissions?.operate;
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const act = async (kind, fn) => {
    setBusy(kind); setErr("");
    try { await fn(); onChange(); } catch (e) { setErr(apiErr(e)); } finally { setBusy(""); }
  };
  const ref = REFERRAL[link.last_referral?.state];
  return (
    <>
      <tr className="mia-row" data-testid={`link-row-${link.id}`}>
        <td>
          <p className="font-medium">{link.label}</p>
          <p className="text-[12px] text-[var(--muted2)] max-w-[240px] truncate" title={link.url}>{link.url}</p>
        </td>
        <td>
          <div className="flex flex-wrap gap-1"><StatusBadge status={link.last_status} /></div>
          {link.last_suspicious && <p className="text-[11px] mt-1" style={{ color: "var(--warn)" }}>Conteúdo parece página estacionada/suspensa</p>}
          {link.last_status === "antibot" && <p className="text-[11px] mt-1 text-[var(--muted)]">O destino respondeu com uma verificação anti-bot; não conta como falha.</p>}
          {link.last_referral?.fragment_params && <p className="text-[11px] mt-1 text-[var(--muted2)]">Parâmetros após "#" não são enviados ao servidor e não podem ser verificados.</p>}
          {link.last_referral?.domain_changed && <p className="text-[11px] mt-1" style={{ color: "var(--warn)" }}>Domínio final: {link.last_referral.final_host}</p>}
          {err && <p className="text-[11px] mt-1" style={{ color: "var(--bad)" }} role="alert">{err}</p>}
        </td>
        <td className="num">{link.last_http_status ?? "—"}</td>
        <td className="num">{link.last_response_ms != null ? `${fmtNum(link.last_response_ms)} ms` : "—"}</td>
        <td className="text-[12px]">{ref ? <Badge tone={ref[0]}>{ref[1]}</Badge> : "—"}</td>
        <td className="mono text-[12px] text-[var(--muted)] whitespace-nowrap">{fmtDate(link.last_check_at)}</td>
        <td className="num">{fmtNum(link.consecutive_failures)}</td>
        <td>
          <div className="flex justify-end gap-1">
            {canOperate && <Button variant="ghost" size="sm" title="Testar agora" aria-label="Testar agora" data-testid={`link-check-${link.id}`} disabled={!!busy}
              onClick={() => act("check", () => api.post(`/admin/links/${link.id}/check`))}>{busy === "check" ? <Spinner size={12} /> : <RefreshCw size={13} />}</Button>}
            <Button variant="ghost" size="sm" title="Histórico" aria-label="Histórico" data-testid={`link-history-${link.id}`} onClick={() => setOpen((o) => !o)}><History size={13} /></Button>
          </div>
        </td>
      </tr>
      {open && <tr><td colSpan={8} className="!p-0 bg-[var(--bg2)]"><HistoryRows linkId={link.id} /></td></tr>}
    </>
  );
}

export default function Links() {
  const canOperate = !!useAuth().user?.permissions?.operate;
  const { data, loading, error, reload } = useAdminData(async (a) => {
    const sync = canOperate ? (await a.post("/admin/links/sync-catalog", { links: CATALOG_LINKS })).data : { rejected: [] };
    const [l, al] = await Promise.all([a.get("/admin/links"), a.get("/admin/links/alerts")]);
    return { ...l.data, alerts: al.data.alerts, rejected: sync.rejected };
  }, [canOperate]);
  const ack = async (id) => { await api.post(`/admin/links/alerts/${id}/ack`).catch(() => {}); reload(); };

  return (
    <div data-testid="links-page">
      <PageHeader title="Monitor de links" meta="Somente leitura. Os links monitorados são exatamente os do catálogo do site e não podem ser alterados por aqui." />
      {data?.rejected?.length > 0 && <p className="ops-help mb-3" role="status" data-testid="links-rejected">{data.rejected.length} link(s) do catálogo não são verificados pela política de segurança: {data.rejected.map((r) => r.key).join(", ")}.</p>}
      {error && <ErrorState message={error} onRetry={reload} />}

      <p className="text-[13px] text-[var(--muted)] mb-5 pl-3 border-l-2 border-[var(--warn)] max-w-3xl" data-testid="links-disclaimer">
        "HTTP acessível" significa apenas que o endereço respondeu sem erro. O monitor não confirma que a afiliação funciona,
        que o cadastro credita a indicação ou que a página carrega corretamente no navegador — confirme isso no painel de cada plataforma.
      </p>

      {data?.alerts?.length > 0 && (
        <Panel title={`Alertas ativos (${data.alerts.length})`} className="mb-4" testid="alerts-panel">
          <table className="ops-table">
            <tbody>
              {data.alerts.map((a) => (
                <tr key={a.id} data-testid={`alert-${a.id}`}>
                  <td><Badge tone={a.status === "open" ? "bad" : "warn"}>{a.status === "open" ? "aberto" : "reconhecido"}</Badge></td>
                  <td className="font-medium">{a.label}</td>
                  <td className="text-[var(--muted)]">{a.reason}</td>
                  <td className="num">{a.failures} falhas</td>
                  <td className="mono text-[12px] text-[var(--muted)]">{fmtDate(a.last_failure_at)}</td>
                  <td className="text-right">{canOperate && a.status === "open" && <Button variant="ghost" size="sm" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}>Reconhecer</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2.5 ops-help border-t border-[var(--border)]">Alertas aparecem neste painel. Envio por e-mail ou Telegram exige configurar um serviço externo (ainda não configurado).</p>
        </Panel>
      )}

      <Panel title="Links monitorados" className="mb-4" testid="links-panel">
        {loading && !data ? <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          : !data?.links.length ? <EmptyState title="Nenhum link monitorado" description="O catálogo do site não possui links verificáveis." />
          : (
            <div className="overflow-x-auto mia-scroll">
              <table className="ops-table ops-stack" data-testid="links-table">
                <thead><tr><th>Link</th><th>Status</th><th className="!text-right">HTTP</th><th className="!text-right">Resposta</th><th>Afiliado</th><th>Último teste</th><th className="!text-right">Falhas seguidas</th><th /></tr></thead>
                <tbody>{data.links.map((l) => <LinkRow key={l.id} link={l} onChange={reload} />)}</tbody>
              </table>
            </div>
          )}
      </Panel>

      {data && <Panel title="Parâmetros do monitor (fixos)"><MonitorInfo settings={data.settings} /></Panel>}
      <p className="ops-help mt-3 max-w-3xl">
        Segurança: apenas http/https nas portas 80 e 443; endereços internos, localhost, IPs privados e de metadados são recusados antes e depois da conexão;
        no máximo 5 redirecionamentos, cada um revalidado; intervalo mínimo de 10 s por domínio e 30 s entre testes manuais do mesmo link.
      </p>
    </div>
  );
}
